/**
 * entity model class
 *
 * This file is part of Minasa
 * (c) Kaden und Partner AG
 */
import { globalVar } from '@/helpers/globals';
import { statusMap } from '@/models/Address';
import BaseModel from './BaseModel';
import BaseCollection from './BaseCollection';
import dayjs from 'dayjs';

export class Location extends BaseModel {
    static STATUS_UNSIGNED = 'unsigned';
    static STATUS_REQUESTED = 'requested';
    static STATUS_REJECTED = 'rejected';
    static STATUS_IN_UNION = 'in_union';

    defaults() {
        return {
            // entity properties
            id: null,
            uuid: null,
            address_id: null,
            is_main_location: null,
            event_organizer_id: null,
            description: null,
            bp_import_id: null,
            bp_import_date: null,
            bp_import_origin: null,
            is_foreign_import: null,
            featured_image_id: null,
            featured_img_crop_config: null,
            status: null,
            rejected_reason: null,
            is_visible: null,
            opening_hours: null,
            created: null,
            modified: null,
            creator_id: null,
            modifier_id: null,
            eventfrog_rubric_id: null,
            accessibility: null,
            accessibility_link: null,

            // view properties
            legal_type: null,
            address_uuid: null,
            event_organizer: null,
            title: null,
            first_name: null,
            surname: null,
            address_supplement: null,
            coord_lon: null,
            coord_lat: null,
            address: null,
            po_box: null,
            zip: null,
            city: null,
            tel: null,
            mobile: null,
            email: null,
            web: null,
            social_media: null,
            mailing_list_ids: null,
            mailing_list_names: null,
            category_ids: null,
            categories_texts: null,
            organizer_group_id: null,
            country_id: null,
            country_name: null,
            country_iso: null,
            creator: null,
            modifier: null,
            status_in_union_mail_sent: null,
            status_rejected_mail_sent: null,
            status_in_union_sender_id: null,
            status_rejected_sender_id: null,
            status_in_union_sender: null,
            status_rejected_sender: null,
            tags: null,
            tag_names: null,
            eventfrog_rubric: null,
        };
    }

    mutations() {
        return {
            // entity properties
            id: (id: number | null) => (Number(id) > 0 ? Number(id) : null),
            address_id: Number,
            event_organizer_id: Number,
            bp_import_id: Number,
            is_visible: Boolean,
            created: dayjs,
            modified: dayjs,
            creator_id: Number,
            modifier_id: Number,
            eventfrog_rubric_id: Number,

            // view properties
            organizer_group_id: Number,
            country_id: Number,
            status_in_union_mail_sent: dayjs,
            status_rejected_mail_sent: dayjs,
            status_in_union_sender_id: Number,
            status_rejected_sender_id: Number,
        };
    }

    entityName() {
        return 'Location';
    }

    get type() {
        return 'location';
    }

    get displayTitle() {
        if (this.first_name || this.surname) {
            let ret = `${this.first_name} ${this.surname}`;
            if (this.title) {
                ret += ` (${this.title})`;
            }
            return ret;
        } else {
            return this.title || '- kein Titel -';
        }
    }

    get titleWithLocation() {
        return [this.displayTitle || null, this.city || null].filter(Boolean).join(', ') || '';
    }

    get is_imported() {
        return this.bp_import_id > 0;
    }

    get humanReadableStatus() {
        const status: string = this.get('status');
        if (status && statusMap[status]) {
            return statusMap[status].text;
        } else {
            return 'neu';
        }
    }

    get status_icon() {
        const status = this.get('status');
        if (status && statusMap[status]) {
            return statusMap[status].icon;
        } else {
            return '';
        }
    }

    get status_icon_color() {
        const status = this.get('status');
        if (status && statusMap[status]) {
            return statusMap[status].icon_color;
        } else {
            return '';
        }
    }

    get thumbImageUrl() {
        if (!this.featured_image_id) {
            return null;
        }
        const id = this.id;
        const conf = globalVar('featured_images_config').location.thumb;
        return `${globalVar('imageroot')}/location/${id}/${conf.ident}.${conf.format}`;
    }
}

export class LocationCollection extends BaseCollection {
    options() {
        return {
            model: Location,
        };
    }

    entityName() {
        return 'Location';
    }
}
